import { create } from 'zustand';
import type { UserNotification, NotificationPriority, NotificationStatus } from '@/types/notification';
import { useToastStore } from '@/store/toast.store';
import { globalNavigate } from '@/utils/global-navigate';
import { safeInternalPath } from '@/utils/safe-url';

export type { UserNotification, NotificationPriority, NotificationStatus };

export interface ToastNotification extends UserNotification {
  isVisible: boolean;
}

function getNotificationRoute(notification: UserNotification): string | null {
  // action_url is server-controlled — only allow app-internal relative routes
  // for router navigation. Reject anything else (absolute URLs, javascript:,
  // etc.) so a crafted notification can't drive navigation off-app or run a
  // dangerous scheme.
  if (notification.action_url) return safeInternalPath(notification.action_url);
  if (!notification.resource_type || !notification.resource_id) return null;
  switch (notification.resource_type) {
    case 'supply_request': return `/supply-requests/${notification.resource_id}`;
    case 'task': return `/tasks/${notification.resource_id}`;
    case 'incident': return `/incidents/${notification.resource_id}`;
    case 'shelter': return `/shelters/${notification.resource_id}`;
    case 'damage_assessment': return `/damage-assessments/${notification.resource_id}`;
    case 'ics_form':
    case 'form': return `/forms/${notification.resource_id}`;
    case 'weather_alert': return '/weather';
    default: return null;
  }
}

interface NotificationState {
  notifications: UserNotification[];
  unreadCount: number;
  toasts: ToastNotification[];
  isPanelOpen: boolean;
  isLoading: boolean;
  /**
   * Message from the most recent FAILED notification fetch, or null when the feed
   * is healthy. Without this the UI cannot tell "the server said you have nothing"
   * from "we never reached the server", and a failed read is drawn as a reassuring
   * empty inbox.
   */
  error: string | null;
  /** True only after a fetch has actually SUCCEEDED for the current session/tenant. */
  hasLoadedOnce: boolean;
  lastFetchedAt: string | null;
  setNotifications: (notifications: UserNotification[]) => void;
  setUnreadCount: (count: number) => void;
  addNotification: (notification: UserNotification) => void;
  addNotifications: (notifications: UserNotification[]) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  dismiss: (id: string) => void;
  togglePanel: () => void;
  closePanel: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setLastFetchedAt: (timestamp: string) => void;
  addToast: (notification: UserNotification) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;
  clearAllState: () => void;
}

const MAX_NOTIFICATIONS = 200;

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  toasts: [],
  isPanelOpen: false,
  isLoading: false,
  error: null,
  hasLoadedOnce: false,
  lastFetchedAt: null,

  // A successful load is the ONLY thing that clears `error` / sets `hasLoadedOnce`.
  // The panel keys its "You're all caught up" copy off these, so a failed read can
  // never be presented as a confirmed-empty inbox.
  setNotifications: (notifications) =>
    set({
      notifications,
      unreadCount: notifications.filter((n) => n.status === 'unread').length,
      error: null,
      hasLoadedOnce: true,
    }),

  setUnreadCount: (count) => set({ unreadCount: count }),

  addNotification: (notification) => {
    const state = get();
    if (state.notifications.find((n) => n.id === notification.id)) return;
    set({
      notifications: [notification, ...state.notifications].slice(0, MAX_NOTIFICATIONS),
      unreadCount: state.unreadCount + (notification.status === 'unread' ? 1 : 0),
    });
    if (notification.status === 'unread') get().addToast(notification);
  },

  addNotifications: (newNotifications) => {
    const state = get();
    const existingIds = new Set(state.notifications.map((n) => n.id));
    const uniqueNew = newNotifications.filter((n) => !existingIds.has(n.id));
    if (uniqueNew.length === 0) return;
    set({
      notifications: [...uniqueNew, ...state.notifications].slice(0, MAX_NOTIFICATIONS),
      unreadCount: state.unreadCount + uniqueNew.filter((n) => n.status === 'unread').length,
    });
    uniqueNew.filter((n) => n.status === 'unread').forEach((n) => get().addToast(n));
  },

  markAsRead: (id) => {
    set((state) => {
      const notification = state.notifications.find((n) => n.id === id);
      if (!notification || notification.status !== 'unread') return state;
      return {
        notifications: state.notifications.map((n) =>
          n.id === id ? { ...n, status: 'read' as const, read_at: new Date().toISOString() } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    });
  },

  markAllAsRead: () => {
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.status === 'unread' ? { ...n, status: 'read' as const, read_at: new Date().toISOString() } : n
      ),
      unreadCount: 0,
    }));
  },

  dismiss: (id) => {
    set((state) => {
      const notification = state.notifications.find((n) => n.id === id);
      const wasUnread = notification?.status === 'unread';
      return {
        notifications: state.notifications.filter((n) => n.id !== id),
        unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
      };
    });
  },

  togglePanel: () => set((state) => ({ isPanelOpen: !state.isPanelOpen })),
  closePanel: () => set({ isPanelOpen: false }),
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
  setLastFetchedAt: (timestamp) => set({ lastFetchedAt: timestamp }),

  addToast: (notification) => {
    const route = getNotificationRoute(notification);
    useToastStore.getState().addToast({
      type: 'info',
      variant: 'rich',
      message: notification.title,
      title: notification.title,
      body: notification.body,
      priority: notification.priority as 'urgent' | 'high' | 'normal' | 'low',
      position: 'bottom-left',
      duration: 0,
      requiresManualDismiss: notification.requires_acknowledgement,
      onClick: route ? () => { globalNavigate(route); } : undefined,
    });
  },

  removeToast: () => { /* no-op */ },
  clearToasts: () => { /* no-op */ },

  clearAllState: () => {
    set({
      notifications: [], unreadCount: 0, toasts: [],
      isPanelOpen: false, isLoading: false, lastFetchedAt: null,
      // Called on tenant switch and logout — the new session has neither a
      // successful load nor a carried-over failure.
      error: null, hasLoadedOnce: false,
    });
  },
}));
